import type { AnalyticsPlugin } from 'analytics';

/**
 * Build-time flag. Statically replaced by Vite — when VITE_E2E is unset
 * (every prod and dev build), this evaluates to `false` and every
 * conditional below is dead-code-eliminated.
 *
 * Set to `'true'` in playwright.config.ts → webServer.env so Playwright
 * runs the app with mocked auth + a window-exposed analytics spy.
 */
export const IS_E2E = import.meta.env.VITE_E2E === 'true';

export interface CapturedEvent {
  name: string;
  properties: Record<string, unknown>;
}

declare global {
  interface Window {
    __analyticsE2EEvents?: CapturedEvent[];
  }
}

interface TrackPayload {
  event: string;
  properties?: Record<string, unknown>;
}

/**
 * Analytics plugin used only when IS_E2E is true — the sole plugin on the
 * instance, so no real destinations (and no ad scripts) load. Pushes every
 * tracked event onto `window.__analyticsE2EEvents` so Playwright fixtures can
 * read them back without touching Mixpanel.
 */
export function createE2EPlugin(): AnalyticsPlugin {
  if (typeof window !== 'undefined') {
    window.__analyticsE2EEvents = window.__analyticsE2EEvents ?? [];
  }
  return {
    name: 'e2e-capture',
    config: {},
    track: ({ payload }: { payload: TrackPayload }) => {
      if (typeof window === 'undefined') return;
      window.__analyticsE2EEvents = window.__analyticsE2EEvents ?? [];
      window.__analyticsE2EEvents.push({ name: payload.event, properties: payload.properties ?? {} });
    },
    reset: () => {
      if (typeof window === 'undefined') return;
      if (window.__analyticsE2EEvents) window.__analyticsE2EEvents.length = 0;
    },
    loaded: () => true,
  };
}
